// A flagged session in miniature, under its Triage row: the trace page's
// compact card of the stretch of the record around the flagged messages, the
// flagged turns pinned; when a finding met a standard, what happened around
// it as one timeline under the card (FindingStory). One read, the card's code loading
// beside it. A click previews the session's workspace (previewTraceCanvas).
import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { readConversationPage } from "../../lib/conversationRead.js";
import { findingStory, storyTimeline } from "../../lib/findingStory.js";
import { previewTraceCanvas } from "../../lib/traceCanvasStore.js";
import { recordWindow } from "../../lib/triageTimeline.js";
import FindingStory, { StoryLoading, pinTitle } from "./FindingStory.jsx";
import { machineOf } from "./reviewSessions.jsx";

const cardModule = () => import("../WorkspaceCardPreview.jsx");
const WorkspaceCardPreview = lazy(cardModule);
// Enough on either side of the flagged message to see what led up to it.
const AROUND = 120;
const FOLLOWING = 60;

// The page around a flagged message, read once per page load. A read costs
// about the same whatever its size, so it starts when the pointer reaches the
// row (prefetchSessionPage) and the opened row usually finds it done.
const pages = new Map();
// A harness or a test answers for the page: (session, messageId) -> turns.
let seeded = null;
export function seedSessionPages(answer) {
  seeded = typeof answer === "function" ? answer : null;
  pages.clear();
}
function pageAround(session, messageId) {
  const key = `${session}:${String(messageId).toLowerCase()}`;
  if (!pages.has(key) && seeded) pages.set(key, Promise.resolve(seeded(session, messageId)));
  if (!pages.has(key)) {
    pages.set(key, readConversationPage({ traceId: session, timeRange: recordWindow(), aroundMessageId: messageId, maxMessages: AROUND, following: FOLLOWING, toolSafety: true })
      // A server that predates the field serves the newest page: not this one.
      .then((read) => (read?.aroundHonoured ? read.turns || [] : null))
      .catch(() => { pages.delete(key); return null; }));
  }
  return pages.get(key);
}
export function prefetchSessionPage(session, findings) {
  const first = findings.flatMap((f) => f.messageIds || [])[0];
  if (session && first) pageAround(session, first);
  cardModule();
}
export function _clearSessionPagesForTests() { pages.clear(); }

// Each finding's turns in these turns: the messages its review cited (and its
// verified quotes', where they carry one).
export function flaggedTurns(findings, turns) {
  const at = new Map();
  (turns || []).forEach((t, i) => { const id = String(t?.messageId || "").toLowerCase(); if (id && !at.has(id)) at.set(id, i); });
  return findings.map((f) => [...new Set((f.messageIds || []).map((id) => at.get(String(id).toLowerCase())).filter((i) => i != null))].sort((a, b) => a - b));
}

function usePageAround(session, messageId) {
  const [page, setPage] = useState(undefined);
  useEffect(() => {
    if (!session || !messageId) { setPage(null); return undefined; }
    let live = true;
    setPage(undefined);
    pageAround(session, messageId).then((turns) => { if (live) setPage(turns); });
    return () => { live = false; };
  }, [session, messageId]);
  return page;
}

const NOTE = "text-[11px] leading-[1.4] text-[color:var(--muted)]";
// Room at the right of the card's head for the story's "Open in workspace",
// which FindingStory lays over it.
const STORY_HEAD_ROOM = 150;

// findings: [{ messageIds, title, tone?, met?, quotes? }], numbered as the list
// numbers them; highlight: the index of the one pointed at, its pins red and
// the rest muted, unless a finding brings its own tone. `around`: the message
// the page centres on instead of the first flagged one. `notes`: more pins,
// in the same shape. `story`: when one of the findings met a standard, what
// happened around them is told under the card (FindingStory), the card
// pinning its moments.
// `told(indexes, story)`: what the host says about those findings, under the
// summary; `toldSummary(story)`: what happened, once above them all;
// `onTold(bool | null)` says whether the story is drawn, null until the page
// is read, for the host to leave its own list of them off meanwhile.
export default function TriageSessionPreview({ session, summary = null, endpointId = null, findings, highlight = null, around = null, notes = null, story = false, told: aboutFindings = null, toldSummary = null, onTold = null }) {
  const firstFlagged = around || findings.flatMap((f) => f.messageIds || [])[0] || null;
  const turns = usePageAround(session, firstFlagged);
  // The page starts mid-session, so its first message is no name for it: the
  // session's own label, else what the row calls it.
  // The person registered to the machine rides on its head, as the row's
  // session column, which the card stands in for when open, names them.
  const registeredUser = machineOf(endpointId)?.user || null;
  const trace = useMemo(() => ({ id: session, sessionId: session, summary: summary || `Session ${String(session).slice(0, 8)}`, endpointId, registeredUser }), [session, summary, endpointId, registeredUser]);
  const told = useMemo(() => (story && turns?.length ? findingStory(turns, findings) : null), [story, turns, findings]);
  const telling = !!told?.met;
  const timeline = useMemo(() => (telling ? storyTimeline(turns, findings, told) : null), [telling, turns, findings, told]);
  const decided = turns !== undefined;
  useEffect(() => { onTold?.(decided ? telling : null); }, [decided, telling, onTold]);
  const pins = useMemo(() => [
    ...flaggedTurns(findings, turns).flatMap((ts, i) => ts.map((turn) => ({
      turn, tone: findings[i].tone || (highlight == null || highlight === i ? "danger" : "calm"), count: i + 1, title: `${i + 1}. ${findings[i].title || "Flagged"}`,
    }))),
    ...(notes?.length ? flaggedTurns(notes, turns).flatMap((ts, i) => ts.map((turn) => ({ turn, tone: notes[i].tone || "calm", count: i + 1, title: notes[i].title || "Note" }))) : []),
  ], [findings, notes, turns, highlight]);
  // Where the card has its pins once laid out, for the story to draw over it.
  const [anchors, setAnchors] = useState(null);
  // The pin of the moment picked in the story, which the card rings.
  const [ringed, setRinged] = useState(null);
  // With a story, the card pins the timeline's moments, each titled for its
  // row: a flagged call on the row of the work, what was said on the
  // conversation. It keeps only their rows and opens zoomed into the story's
  // stretch, a message either side.
  const storyPins = useMemo(() => (timeline?.rows || []).flatMap((r, i) => {
    const pin = { turn: r.turn, count: 1, title: pinTitle(i), ...(pinTitle(i) === ringed ? { ring: true } : {}) };
    if (r.kind === "act") return [{ ...pin, tone: r.tone, prefer: "work" }];
    if (r.kind === "say" || r.kind === "said") return [{ ...pin, tone: r.cited ? r.tone || "warn" : "calm", prefer: "talk" }];
    return [];
  }), [timeline, ringed]);
  const lens = useMemo(() => {
    const at = (r) => (r.kind === "calls" ? r.items[0].turn : r.turn);
    const placed = (timeline?.rows || []).filter((r) => r.kind !== "gap");
    if (!placed.length) return null;
    const time = (i) => turns[Math.max(0, Math.min(turns.length - 1, i))]?.messageAt;
    const first = at(placed[0]), last = placed[placed.length - 1].kind === "calls" ? placed[placed.length - 1].items.at(-1).turn : at(placed[placed.length - 1]);
    const t0 = time(first - 1) ?? time(first);
    const t1 = time(last + 1) ?? time(last);
    return Number.isFinite(t0) && Number.isFinite(t1) && t1 > t0 ? { t0, t1 } : null;
  }, [timeline, turns]);
  const openAt = (flag) => {
    previewTraceCanvas({ id: session, title: summary || "", message: flag.messageId });
  };
  const loading = story
    ? <span data-lite-preview="loading"><StoryLoading /></span>
    : <span data-lite-preview="loading" aria-busy="true" className={NOTE}>Loading the session{"\u2026"}</span>;
  if (turns === undefined) return loading;
  if (!turns?.length) return <span data-lite-preview="error" className={NOTE}>Couldn&rsquo;t load this session&rsquo;s trace.</span>;
  return (
    <div data-lite-preview="card" className="min-w-0">
      {telling ? (
        <FindingStory story={told} timeline={timeline} turns={turns} anchors={anchors} findingsFor={aboutFindings} summaryFor={toldSummary} onOpen={openAt} onRing={setRinged}>
          <Suspense fallback={loading}>
            <WorkspaceCardPreview trace={trace} turns={turns} pins={storyPins} review={false} onAnchors={setAnchors} lens={lens} onlyPinned headRoom={STORY_HEAD_ROOM} />
          </Suspense>
        </FindingStory>
      ) : (
        <Suspense fallback={loading}>
          <WorkspaceCardPreview trace={trace} turns={turns} pins={pins} review={false} onOpen={() => previewTraceCanvas({ id: session, title: summary || "" })} />
        </Suspense>
      )}
    </div>
  );
}
